import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@lib/useAuth';
import Button from '@components/Button';
import Container from '@components/Container';
import { useLeaderboardData } from '@lib/useLeaderboardData';
import {
  buildLeaderboardDisplayEntries,
  DEFAULT_LEADERBOARD_AVATAR,
  type LeaderboardDisplayRow,
} from '@lib/leaderboardDisplay';
import { formatHoursMinutes } from '@lib/formatTime';

const LEADERBOARD_RANKS_STORAGE_KEY = 'leaderboard-ranks-v1';

const readStoredLeaderboardRanks = (): Record<string, number> => {
  if (typeof window === 'undefined') return {};
  try {
    const stored = window.localStorage.getItem(LEADERBOARD_RANKS_STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') return {};

    const sanitized: Record<string, number> = {};
    Object.entries(parsed).forEach(([userId, value]) => {
      const parsedRank = Number(value);
      if (Number.isFinite(parsedRank)) {
        sanitized[userId] = parsedRank;
      }
    });

    return sanitized;
  } catch (storageError) {
    console.warn('Unable to read stored leaderboard ranks', storageError);
    return {};
  }
};

const persistLeaderboardRanks = (ranks: Record<string, number>) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      LEADERBOARD_RANKS_STORAGE_KEY,
      JSON.stringify(ranks)
    );
  } catch (storageError) {
    console.warn('Unable to persist leaderboard ranks', storageError);
  }
};

// For leaderboard we prefer hours/minutes (e.g. "1 hour 59 minutes").
// Use formatHoursMinutes(totalSeconds) when rendering.

const ArrowIcon = ({
  direction,
  className,
}: {
  direction: 'up' | 'down';
  className?: string;
}) => (
  <svg
    role="presentation"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    {direction === 'up' ? (
      <>
        <path
          d="M10 15V5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6 9l4-4 4 4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ) : (
      <>
        <path
          d="M10 5v10"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14 11l-4 4-4-4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    )}
  </svg>
);

const PositionDeltaIndicator = ({ delta }: { delta: number }) => {
  if (!delta) return null;
  const isPositive = delta > 0;
  const direction = isPositive ? 'up' : 'down';
  const colorClass = isPositive ? 'text-emerald-600' : 'text-rose-600';
  const absDelta = Math.abs(delta);

  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-semibold ${colorClass}`}
      aria-live="polite"
    >
      <ArrowIcon direction={direction} className="h-3.5 w-3.5" />
      <span>{absDelta}</span>
      <span className="sr-only">
        {`Moved ${direction} ${absDelta} place${absDelta === 1 ? '' : 's'}`}
      </span>
    </span>
  );
};

function Leaders() {
  const { user } = useAuth();
  const [leaderboardTab, setLeaderboardTab] = useState<'minutes' | 'streak'>(
    'minutes'
  );
  const {
    entries,
    profiles,
    loading: isLoadingLeaderboard,
    error: leaderboardError,
  } = useLeaderboardData(100);

  const previousRanksRef = useRef<Record<string, number>>(
    readStoredLeaderboardRanks()
  );

  const [positionChanges, setPositionChanges] = useState<
    Record<string, number>
  >({});

  useEffect(() => {
    if (!entries || entries.length === 0) {
      setPositionChanges({});
      return;
    }

    const nextRanks: Record<string, number> = {};
    const movements: Record<string, number> = {};

    entries.forEach((entry, index) => {
      if (!entry.user_id) return;
      const currentRank =
        typeof entry.rank === 'number' && entry.rank > 0
          ? entry.rank
          : index + 1;
      nextRanks[entry.user_id] = currentRank;
      const previousRank = previousRanksRef.current[entry.user_id];
      movements[entry.user_id] =
        typeof previousRank === 'number' ? previousRank - currentRank : 0;
    });

    previousRanksRef.current = nextRanks;
    persistLeaderboardRanks(nextRanks);
    setPositionChanges(movements);
  }, [entries]);

  const displayedLeaderboard = useMemo<LeaderboardDisplayRow[]>(
    () =>
      buildLeaderboardDisplayEntries({
        entries,
        profiles,
        leaderboardTab,
        currentUserId: user?.id ?? null,
      }),
    [entries, profiles, leaderboardTab, user?.id]
  );

  return (
    <Container className="pb-24 pt-10">
      <div className="pb-10 text-center">
        <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
          Global Leaderboard
        </h1>

        <p className="mt-4 text-base text-gray-600">
          A global home for the Champions of Stillness—those who breathe, rise,
          and lead.
        </p>
      </div>

      <div className="mx-auto max-w-5xl">
        <div className="card">
          <div className="space-y-2">
            <div className="mb-3 flex items-center justify-center gap-2">
              <Button
                type="button"
                size="sm"
                variant={leaderboardTab === 'minutes' ? 'primary' : 'secondary'}
                className={leaderboardTab === 'minutes' ? 'item-selected' : ''}
                onClick={() => setLeaderboardTab('minutes')}
              >
                Top Time
              </Button>
              <Button
                type="button"
                size="sm"
                variant={leaderboardTab === 'streak' ? 'primary' : 'secondary'}
                className={leaderboardTab === 'streak' ? 'item-selected' : ''}
                onClick={() => setLeaderboardTab('streak')}
              >
                Top Day Streak
              </Button>
            </div>

            {isLoadingLeaderboard ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-white/60 px-6 py-6 text-center text-sm text-gray-600">
                Loading leaderboard...
              </div>
            ) : leaderboardError ? (
              <div className="rounded-lg border border-dashed border-red-200 bg-red-50/80 px-6 py-6 text-center text-sm text-red-700">
                {leaderboardError}
              </div>
            ) : displayedLeaderboard.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-white/60 px-6 py-6 text-center text-sm text-gray-600">
                No leaderboard entries yet. Sessions shared to the community
                will appear here.
              </div>
            ) : (
              displayedLeaderboard.map((entry) => {
                const isCurrentUser =
                  user?.id && entry.userId && entry.userId === user.id;
                const profileLink = entry.profileSlug
                  ? `/profile/${encodeURIComponent(entry.profileSlug)}`
                  : entry.userId && user?.id && entry.userId === user.id
                    ? '/profile'
                    : null;
                const positionDelta = entry.userId
                  ? (positionChanges[entry.userId] ?? 0)
                  : 0;
                return (
                  <div
                    key={`${entry.userId ?? 'rank'}-${entry.rank}`}
                    className={`flex flex-col gap-3 rounded-lg p-3 sm:flex-row sm:items-center sm:justify-between ${
                      isCurrentUser
                        ? 'bg-primary-50 font-semibold'
                        : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                      <span className="text-lg font-bold text-gray-500">
                        #{entry.rank}
                      </span>
                      <div className="flex items-center gap-3">
                        {entry.profilePictureUrl &&
                        entry.profilePictureUrl !==
                          DEFAULT_LEADERBOARD_AVATAR ? (
                          <img
                            src={entry.profilePictureUrl}
                            alt={entry.name}
                            loading="lazy"
                            className="h-10 w-10 rounded-full border border-gray-200 object-cover"
                          />
                        ) : // If this entry is the current user and they have no custom
                        // profile picture, make the emoji clickable and show a
                        // helpful title that lets them create an AI avatar.
                        isCurrentUser &&
                          (!entry.profilePictureUrl ||
                            entry.profilePictureUrl ===
                              DEFAULT_LEADERBOARD_AVATAR) ? (
                          <Link
                            to="/settings?tab=profile"
                            title="Click me to create AI profile avatar"
                            aria-label="Create AI profile picture"
                            className="flex h-10 w-10 items-center justify-center rounded-full border-2 bg-white"
                          >
                            <div
                              className={`flex h-9 w-9 items-center justify-center rounded-full ${
                                entry.profileIsPrivate
                                  ? 'border-4 border-rose-600'
                                  : 'border-4 border-emerald-600'
                              }`}
                              aria-hidden="true"
                            >
                              <span className="text-2xl">😮‍💨</span>
                            </div>
                          </Link>
                        ) : (
                          <div
                            className={`flex h-10 w-10 items-center justify-center rounded-full border-2 bg-white`}
                          >
                            <div
                              className={`flex h-9 w-9 items-center justify-center rounded-full ${
                                entry.profileIsPrivate
                                  ? 'border-4 border-rose-600'
                                  : 'border-4 border-emerald-600'
                              }`}
                              aria-hidden="true"
                            >
                              <span className="text-2xl">😮‍💨</span>
                            </div>
                          </div>
                        )}
                        <div>
                          {profileLink ? (
                            <Link
                              to={profileLink}
                              className="text-sm font-medium text-primary-600 underline-offset-2 hover:underline focus-visible:underline"
                            >
                              {entry.name}
                            </Link>
                          ) : (
                            <div className="text-sm">{entry.name}</div>
                          )}
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            {entry.sessions} sessions
                            {positionDelta !== 0 ? (
                              <PositionDeltaIndicator delta={positionDelta} />
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-end text-right text-sm font-semibold text-primary-600 sm:w-36 sm:justify-end">
                      {leaderboardTab === 'minutes'
                        ? formatHoursMinutes(
                            entry.totalSeconds || entry.totalMinutes * 60
                          )
                        : `${entry.dayStreak ?? 0}d`}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Container>
  );
}

export default Leaders;
